"use client";

import { useState } from "react";
import { saveFaq } from "../actions";
import type { Faq } from "../types";
import type { FaqGroupRecord } from "../types";
import { LanguageBar, langProps, type EditLang } from "./locale";
import { SaveBar, useSave } from "./SaveBar";
import { BTN_GHOST, CARD, ConfirmButton, IconButton, INPUT, PageTitle } from "./ui";

function moved<T>(list: T[], i: number, by: number) {
  const next = [...list];
  const [item] = next.splice(i, 1);
  next.splice(i + by, 0, item);
  return next;
}

/** Questions and answers with add, remove and reorder (FAQ page groups and service pages). */
export function FaqItemsEditor({ value, onChange, lang }: { value: Faq[]; onChange: (value: Faq[]) => void; lang?: EditLang }) {
  const text = lang && langProps(lang);
  const set = (i: number, changes: Partial<Faq>) => onChange(value.map((f, j) => (j === i ? { ...f, ...changes } : f)));
  return (
    <div className="flex flex-col gap-3">
      {value.map((f, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-xl border border-midnight/10 bg-offwhite p-3">
          <div className="flex items-center gap-2">
            <input aria-label={`Question ${i + 1}`} value={f.q} onChange={(e) => set(i, { q: e.target.value })} placeholder="Question" className={`${INPUT} font-bold`} {...text} />
            <IconButton label="Move up" disabled={i === 0} onClick={() => onChange(moved(value, i, -1))}>↑</IconButton>
            <IconButton label="Move down" disabled={i === value.length - 1} onClick={() => onChange(moved(value, i, 1))}>↓</IconButton>
            <IconButton label="Remove question" onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</IconButton>
          </div>
          <textarea aria-label={`Answer ${i + 1}`} rows={3} value={f.a} onChange={(e) => set(i, { a: e.target.value })} placeholder="Answer: start with the direct answer." className={`${INPUT} resize-y leading-relaxed`} {...text} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { q: "", a: "" }])} className={`${BTN_GHOST} self-start`}>
        + Add question
      </button>
    </div>
  );
}

/** The FAQ page: groups of questions, in page order. Each group has its English and its Arabic questions; the order of
 * the groups is shared. A group appears on the Arabic FAQ page once it has an Arabic title and questions. */
export function FaqEditor({ groups: initial }: { groups: FaqGroupRecord[] }) {
  const [groups, setGroups] = useState(initial);
  const [lang, setLang] = useState<EditLang>("en");
  const { pending, notice, dirty, markDirty, run } = useSave();
  const update = (next: FaqGroupRecord[]) => {
    markDirty();
    setGroups(next);
  };
  const set = (i: number, changes: Partial<FaqGroupRecord>) => update(groups.map((g, j) => (j === i ? { ...g, ...changes } : g)));
  // The group's text in the language being edited.
  const title = (g: FaqGroupRecord) => (lang === "en" ? g.title : (g.ar?.title ?? ""));
  const items = (g: FaqGroupRecord) => (lang === "en" ? g.items : (g.ar?.items ?? []));
  const setText = (i: number, changes: { title?: string; items?: Faq[] }) =>
    set(i, lang === "en" ? changes : { ar: { title: groups[i].ar?.title ?? "", items: groups[i].ar?.items ?? [], ...changes } });
  const arabicMissing = groups.some((g) => !g.ar?.title?.trim() || !g.ar.items.length);

  return (
    <div>
      <PageTitle title="FAQ" />
      <LanguageBar lang={lang} onChange={setLang} missing={{ ar: arabicMissing }} arabicNote="Arabic questions for /ar/faq. A group shows there once it has an Arabic title and questions. Add, remove and reorder groups on either tab." />
      <div className="flex flex-col gap-6">
        {groups.map((g, i) => (
          <section key={g.id} className={`${CARD} flex flex-col gap-4`}>
            <div className="flex flex-wrap items-center gap-2">
              <input aria-label={`Group ${i + 1} title`} value={title(g)} onChange={(e) => setText(i, { title: e.target.value })} placeholder={lang === "ar" ? g.title || "Group title" : "Group title"} className={`${INPUT} min-w-0 flex-1 basis-48 text-lg font-bold`} {...langProps(lang)} />
              <IconButton label="Move group up" disabled={i === 0} onClick={() => update(moved(groups, i, -1))}>↑</IconButton>
              <IconButton label="Move group down" disabled={i === groups.length - 1} onClick={() => update(moved(groups, i, 1))}>↓</IconButton>
              <ConfirmButton label="Delete group" question="Delete this group?" onConfirm={() => update(groups.filter((_, j) => j !== i))} />
            </div>
            <FaqItemsEditor key={lang} value={items(g)} onChange={(next) => setText(i, { items: next })} lang={lang} />
          </section>
        ))}
        <button type="button" onClick={() => update([...groups, { id: `group-${Date.now().toString(36)}`, title: "", items: [{ q: "", a: "" }], ar: { title: "", items: [{ q: "", a: "" }] } }])} className={`${BTN_GHOST} self-start`}>
          + Add group
        </button>
      </div>
      <SaveBar onSave={() => run(() => saveFaq(groups))} pending={pending} dirty={dirty} notice={notice} />
    </div>
  );
}
